import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { axioKit } from "../../../../utilities";

const url = "/diagnostics/clinic/settlements";

const normalizeDispenses = (dispenses = []) =>
  (Array.isArray(dispenses) ? dispenses : [])
    .map((item) => ({
      inventoryId: String(item?.inventoryId || ""),
      qty: Number(item?.qty || 0),
    }))
    .filter((item) => item.inventoryId && item.qty > 0);

const initialState = {
  collections: [],
  cart: [],
  privilege: 0,
  healthCardContributions: {},
  payment: "cash",
  cash: 0,
  formSubmitted: false,
  isSuccess: false,
  isLoading: false,
  message: "",
};

export const BROWSE = createAsyncThunk(
  `${url}`,
  ({ token, data }, thunkAPI) => {
    try {
      return axioKit.universal(`${url}/browse`, token, data);
    } catch (error) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();

      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const SAVE = createAsyncThunk(`${url}/save`, (form, thunkAPI) => {
  try {
    return axioKit.save(url, form.data, form.token);
  } catch (error) {
    const message =
      (error.response && error.response.data && error.response.data.message) ||
      error.message ||
      error.toString();

    return thunkAPI.rejectWithValue(message);
  }
});

export const UPDATE = createAsyncThunk(`${url}/update`, (form, thunkAPI) => {
  try {
    return axioKit.update(url, form.data, form.token);
  } catch (error) {
    const message =
      (error.response && error.response.data && error.response.data.message) ||
      error.message ||
      error.toString();

    return thunkAPI.rejectWithValue(message);
  }
});

export const DESTROY = createAsyncThunk(
  `${url}/destroy`,
  ({ data, token }, thunkAPI) => {
    try {
      return axioKit.destroy(url, data, token);
    } catch (error) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();

      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const reduxSlice = createSlice({
  name: url,
  initialState,
  reducers: {
    AddToCart: (state, { payload }) => {
      const item = payload || {};
      const exists = state.cart.some(
        ({ _id }) => String(_id || "") === String(item?._id || ""),
      );

      if (exists) return;

      state.cart.push({ ...item, qty: 1, dispenses: [] });
    },
    SetCART: (state, { payload }) => {
      state.cart = payload;
    },
    ChangeQty: (state, { payload }) => {
      const { index, value } = payload;
      state.cart[index].qty = value; // ✅ safe ito sa RTK
    },

    SetItemDispenses: (state, { payload }) => {
      const { index, dispenses = [] } = payload || {};
      if (!state.cart[index]) return;
      state.cart[index].dispenses = normalizeDispenses(dispenses);
    },
    SetPrivilege: (state, { payload }) => {
      state.privilege = Number(payload || 0);
    },
    SetHealthCardContribution: (state, { payload }) => {
      const key = String(payload?.key || "");
      if (!key) return;

      state.healthCardContributions[key] = Math.max(
        0,
        Number(payload?.value || 0),
      );
    },
    RemoveFromCart: (state, { payload }) => {
      state.cart = state.cart.filter((_, index) => index !== payload);
    },
    SetPayment: (state, { payload }) => {
      state.payment = payload || "cash";
    },
    SetCash: (state, { payload }) => {
      state.cash = Number(payload || 0);
    },
    ResetBillingDraft: (state) => {
      state.cart = [];
      state.privilege = 0;
      state.healthCardContributions = {};
      state.payment = "cash";
      state.cash = 0;
    },
    RESET: (state) => {
      state.formSubmitted = false;
      state.isSuccess = false;
      state.message = "";
    },
    /**
     *  for pagination
     */
  },
  extraReducers: (builder) => {
    builder
      .addCase(BROWSE.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
        state.message = "";
      })
      .addCase(BROWSE.fulfilled, (state, action) => {
        const { success, payload = [] } = action.payload;

        state.collections = payload;
        state.totalPages = Math.ceil(payload?.length / state.maxPage) || 1;
        state.activePage = Math.min(state.activePage, state.totalPages);
        state.isSuccess = success;
        state.isLoading = false;
      })

      .addCase(BROWSE.rejected, (state, action) => {
        const { error } = action;
        state.message = error.message;
        state.isLoading = false;
      })

      .addCase(SAVE.pending, (state) => {
        state.formSubmitted = true;
        state.isSuccess = false;
        state.message = "";
      })
      .addCase(SAVE.fulfilled, (state, action) => {
        const { payload } = action.payload;
        if (state.collections.length > 0) {
          state.collections.unshift(payload);
          state.filtered.unshift(payload);
        }
        localStorage.setItem("clinicStub", JSON.stringify(payload));
        window.open(
          "/printout/clinicStub",
          "Claim Stub",
          "top=100px,left=100px,width=550px,height=750px"
        );
        state.isSuccess = true;
        state.formSubmitted = false;
      })
      .addCase(SAVE.rejected, (state, action) => {
        const { error } = action;
        state.message = error.message;
        state.formSubmitted = false;
        state.isSuccess = false;
      })
      .addCase(UPDATE.pending, (state) => {
        state.isSuccess = false;
        state.formSubmitted = true;
        state.message = "";
      })
      .addCase(UPDATE.fulfilled, (state, action) => {
        const { success, payload } = action;
        const index = state.collections.findIndex(
          (item) => item._id === payload._id
        );
        state.collections[index] = payload;
        state.filtered = state.collections.filter(
          ({ sched }) => sched === state.activeSched
        );
        state.showModal = false;
        state.formSubmitted = false;
        state.message = success;
        state.isSuccess = true;
      })
      .addCase(UPDATE.rejected, (state, action) => {
        const { error } = action;
        state.message = error.message;
        state.isSuccess = false;
        state.formSubmitted = false;
      })
      .addCase(DESTROY.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
        state.message = "";
      })
      .addCase(DESTROY.fulfilled, (state, action) => {
        const { success } = action;
        const index = state.collections.findIndex(
          (item) => item?._id === action.payload
        );
        state.collections.splice(index, 1);
        state.message = success;
        state.isSuccess = true;
        state.isLoading = false;
      })
      .addCase(DESTROY.rejected, (state, action) => {
        const { error } = action;
        state.message = error.message;
        state.isLoading = false;
      });
  },
});

export const {
  AddToCart,
  SetCART,
  ChangeQty,
  SetItemDispenses,
  SetPrivilege,
  SetHealthCardContribution,
  RemoveFromCart,
  SetPayment,
  SetCash,
  ResetBillingDraft,
  RESET,
} = reduxSlice.actions;

export default reduxSlice.reducer;
